import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falAward } from 'shared/@generated/icons/falAward';
import { falComment } from 'shared/@generated/icons/falComment';
import { falEnvelope } from 'shared/@generated/icons/falEnvelope';
import { falHandshake } from 'shared/@generated/icons/falHandshake';
import { falRectangleVerticalHistory } from 'shared/@generated/icons/falRectangleVerticalHistory';
import { falShieldKeyhole } from 'shared/@generated/icons/falShieldKeyhole';
import { falUserCircle } from 'shared/@generated/icons/falUserCircle';
import { Pressable, View } from 'react-native';
import React from 'react';
import { H1, H2 } from 'shared/components/title';
import { useTranslation } from 'react-i18next';
import { Paginator, Props, Tag, TagType } from 'shared/@types';
import { Post } from 'shared/@generated/models';
import { apiRoute } from 'shared/library/api-route';
import { Icon } from 'shared/components/icon';
import { Text } from 'shared/components/text';
import { FaqCard } from 'shared/components/faq-card';
import { Link } from 'shared/components/link';
import { brevo } from 'shared/library/brevo';
import { useRoute } from 'shared/library/routes/route';
import { PageBuilder } from 'shared/library/page-builder';

interface HelpScreenProps extends Props {
  posts: Paginator<Post>;
  tags: Tag[];
}

const HelpScreen = ({ posts, tags }: HelpScreenProps) => {
  const { t } = useTranslation('screens');
  const route = useRoute();

  const icons = {
    petpay: falShieldKeyhole,
    listing: falRectangleVerticalHistory,
    'buying-a-pet': falHandshake,
    'breeder-account': falAward,
    'user-account': falUserCircle,
  };

  return (
    <View className={'p-3 md:py-12'}>
      <View className={`mx-auto w-full max-w-screen-md`}>
        <H1
          title={t('help.title')}
          subtitle={t('help.description')}
          marginTop={false}
          center={true}
          hideTitleWhenNativeHeader={true}
        />

        <H2 title={t('help.common_topics')} />

        <View className={'gap-3 md:grid md:grid-cols-2 md:gap-6'}>
          {tags
            .filter((tag) => tag.slug && icons.hasOwnProperty(tag.slug.en))
            .map((tag) => (
              <Link
                key={tag.slug.en}
                href={route('help.show', { tag: tag.slug })}
                asBlock={true}
                containerClassName={'w-full'}
              >
                <View
                  className={
                    'border-brand-brown-dark/15 w-full flex-row items-center gap-3 rounded-lg border bg-white p-3 hover:opacity-75'
                  }
                >
                  <View className={'bg-brand-cream rounded-2xl p-3'}>
                    <Icon
                      icon={icons[tag.slug.en as keyof typeof icons]}
                      className={'text-brand-brown-dark size-8'}
                    />
                  </View>

                  <View className={'flex-1'}>
                    <Text className={'text-brand-brown-dark font-bold'}>{tag.name}</Text>
                    <Text className={'text-brand-brown-dark/70 text-sm'}>{tag.description}</Text>
                  </View>
                </View>
              </Link>
            ))}
        </View>

        <H2 title={t('help.common_questions')} />

        <View className={'border-brand-brown-dark/15 rounded-lg border bg-white px-3'}>
          {posts.data.map((post) => (
            <FaqCard key={post.slug.en} post={post} />
          ))}

          <Text className={'text-brand-brown-dark/70 py-6 text-center text-sm'}>
            {t('help.cant_find_question')}
          </Text>
        </View>

        <H2 title={t('help.contact_us')} />

        <View className={'gap-3 md:grid md:grid-cols-2 md:gap-6'}>
          {brevo.hasLiveChat && (
            <Pressable className={'w-full'} onPress={() => brevo.openChat()}>
              <View
                className={
                  'border-brand-brown-dark/15 w-full flex-row items-center gap-3 rounded-lg border bg-white p-3 hover:opacity-75'
                }
              >
                <View className={'bg-brand-green-light rounded-2xl p-3'}>
                  <Icon icon={falComment} className={'text-brand-green-dark size-8'} />
                </View>

                <View className={'flex-1'}>
                  <Text className={'text-brand-brown-dark font-bold'}>{t('help.live_chat')}</Text>
                  <Text className={'text-brand-brown-dark/70 mt-0.5 text-sm'}>
                    {t('help.live_chat_description')}
                  </Text>
                </View>

                <Icon icon={falAngleRight} className={'text-brand-brown-dark/50'} />
              </View>
            </Pressable>
          )}

          <Link href={'mailto:support@getapet.com'} asBlock={true} containerClassName={'w-full'}>
            <View
              className={
                'border-brand-brown-dark/15 w-full flex-row items-center gap-3 rounded-lg border bg-white p-3 hover:opacity-75'
              }
            >
              <View className={'bg-brand-green-light rounded-2xl p-3'}>
                <Icon icon={falEnvelope} className={'text-brand-green-dark size-8'} />
              </View>

              <View className={'flex-1'}>
                <Text className={'text-brand-brown-dark font-bold'}>{t('help.email')}</Text>
                <Text className={'text-brand-brown-dark/70 mt-0.5 text-sm'}>
                  {t('help.email_description')}
                </Text>
              </View>

              <Icon icon={falAngleRight} className={'text-brand-brown-dark/50'} />
            </View>
          </Link>
        </View>
      </View>
    </View>
  );
};

HelpScreen.pageBuilder = new PageBuilder<HelpScreenProps>()
  .request(({ req }) =>
    req<{ posts: Paginator<Post> }>(apiRoute('posts.index'), { type: TagType.Faq }),
  )
  .request(({ req }) => req<{ tags: Tag[] }>(apiRoute('tags.index'), { type: TagType.Faq }));

export { HelpScreen };
export type { HelpScreenProps };
